import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
const ToastContext = createContext(undefined);
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const addToast = useCallback((toast) => {
        const id = Math.random().toString(36).substring(7);
        const newToast = { ...toast, id };
        setToasts((prev) => [...prev, newToast]);
        // Auto-remove after duration (default 5 seconds)
        const duration = toast.duration ?? 5000;
        setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
    }, []);
    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);
    return (_jsxs(ToastContext.Provider, { value: { toasts, addToast, removeToast }, children: [children, _jsx(ToastContainer, { toasts: toasts, onRemove: removeToast })] }));
}
export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
function ToastContainer({ toasts, onRemove }) {
    if (toasts.length === 0)
        return null;
    return (_jsx("div", { className: "fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm", children: toasts.map((toast) => (_jsx(ToastItem, { toast: toast, onRemove: onRemove }, toast.id))) }));
}
function ToastItem({ toast, onRemove }) {
    const Icon = toast.variant === 'error' ? AlertCircle : CheckCircle;
    return (_jsxs("div", { className: cn('flex items-start gap-2 rounded-lg border p-3 shadow-lg transition-all animate-in slide-in-from-right-full', toast.variant === 'error'
            ? 'bg-red-50 border-red-200 text-red-800'
            : toast.variant === 'success'
                ? 'bg-green-50 border-green-200 text-green-800'
                : 'bg-white border-gray-200 text-gray-800'), children: [_jsx(Icon, { className: cn('h-4 w-4 mt-0.5 flex-shrink-0', toast.variant === 'error' ? 'text-red-600' : 'text-green-600') }), _jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("p", { className: "text-sm font-medium", children: toast.title }), toast.description && (_jsx("p", { className: "text-xs mt-1 opacity-80", children: toast.description }))] }), _jsx("button", { onClick: () => onRemove(toast.id), className: "flex-shrink-0 text-gray-400 hover:text-gray-600", children: _jsx(X, { className: "h-4 w-4" }) })] }));
}
//# sourceMappingURL=toast.js.map